/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  IconButton
} from '@mui/material';
import { 
  FirstPage, 
  LastPage, 
  KeyboardArrowLeft, 
  KeyboardArrowRight,
  Search as SearchIcon 
} from '@mui/icons-material';
import useTableSort from '../../hooks/useTableSort';
import SortableTableHeader from '../shared/SortableTableHeader';
import useFacility from '../../hooks/useFacility';
import { toast } from 'react-toastify';

const ResourceManagement = () => {
  const { facilityPreference, loading: prefLoading } = useFacility();
  const [resources, setResources] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState('all');
  
  useEffect(() => {
    if (!prefLoading && facilityPreference) {
      setSelectedFacility(facilityPreference);
    }
  }, [facilityPreference, prefLoading]);
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [newResource, setNewResource] = useState({
    resource: '',
    description: '',
    facility: '',
    unit: ''
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredResources, setFilteredResources] = useState([]);

  const { sortedData, sortColumn, sortOrder, handleSort, resetSort } = useTableSort(
    filteredResources,
    { defaultSortColumn: 'resource', defaultSortOrder: 'asc' }
  );

  const fetchData = useCallback(async () => {
    try {
      const [resourcesRes, facilitiesRes, unitsRes] = await Promise.all([
        axios.get('/api/resources'),
        axios.get('/api/facilities'),
        axios.get('/api/units')
      ]);
      
      let fetchedResources = resourcesRes.data;
      if (selectedFacility !== 'all') {
        fetchedResources = fetchedResources.filter(
          resource => resource.facility?._id === selectedFacility
        );
      }
      
      setResources(fetchedResources);
      setFilteredResources(fetchedResources);
      setFacilities(facilitiesRes.data);
      setUnits(unitsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error details:', error);
      setError('Failed to fetch data');
      setLoading(false);
    }
  }, [selectedFacility]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const filtered = resources.filter(resource => 
      Object.values(resource).some(value => 
        value && value.toString().toLowerCase().includes(searchQuery.toLowerCase())
      ) ||
      resource.facility?.facility_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (resource.unit?.base_unit || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredResources(filtered);
    setPage(0);
  }, [searchQuery, resources]);

  const startIndex = page * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, sortedData.length);
  const paginatedResources = sortedData.slice(startIndex, endIndex);

  const handleChangePage = (newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter units by the selected facility in the create/edit dialog
  const getUnitsForFacility = (facilityId) => {
    if (!facilityId) return units;
    return units.filter(u => {
      const uFacId = u.facility?._id || u.facility;
      return uFacId === facilityId;
    });
  };

  const handleCreate = async () => {
    try {
      if (!newResource.resource || !newResource.description || !newResource.facility || !newResource.unit) {
        alert('Please fill in all required fields');
        return;
      }

      const resourceData = {
        resource: newResource.resource.trim(),
        description: newResource.description.trim(),
        facility: newResource.facility,
        unit: newResource.unit
      };

      await axios.post('/api/resources', resourceData);
      await fetchData();
      toast.success('Resource created successfully');
      setNewResource({ resource: '', description: '', facility: '', unit: '' });
    } catch (error) {
      console.error('Error creating resource:', error);
      toast.error(error.response?.data?.error || 'Failed to create resource');
    }
  };

  const handleUpdate = async () => {
    try {
      if (!editingResource.resource || !editingResource.description) {
        alert('Please fill in all required fields');
        return;
      }

      const facilityVal = typeof editingResource.facility === 'object' ? editingResource.facility._id : editingResource.facility;
      const unitVal = typeof editingResource.unit === 'object' ? editingResource.unit._id : editingResource.unit;

      if (!facilityVal || !unitVal) {
        alert('Facility and Unit are required');
        return;
      }

      const resourceData = {
        resource: editingResource.resource.trim(),
        description: editingResource.description.trim(),
        facility: facilityVal,
        unit: unitVal
      };

      await axios.put(`/api/resources/${editingResource._id}`, resourceData);
      await fetchData();
      setEditDialogOpen(false);
      setEditingResource(null);
    } catch (error) {
      console.error('Error updating resource:', error);
      alert(error.response?.data?.error || 'Failed to update resource');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this resource?')) {
      try {
        await axios.delete(`/api/resources/${id}`);
        await fetchData();
      } catch (error) {
        console.error('Error deleting resource:', error);
        alert('Failed to delete resource');
      }
    }
  };

  // Helpers for edit dialog — get current facility ID even when populated
  const getEditFacilityId = () => {
    if (!editingResource?.facility) return '';
    return typeof editingResource.facility === 'object' ? editingResource.facility._id : editingResource.facility;
  };
  const getEditUnitId = () => {
    if (!editingResource?.unit) return '';
    return typeof editingResource.unit === 'object' ? editingResource.unit._id : editingResource.unit;
  };

  const renderPaginationControls = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <span>Show</span>
        <Select value={rowsPerPage} onChange={handleChangeRowsPerPage} sx={{ mx: 1 }} size="small">
          {[10, 25, 50, 100].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </Select>
        <span>entries</span>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton aria-label="First page" onClick={() => handleChangePage(0)} disabled={page === 0}><FirstPage /></IconButton>
        <IconButton aria-label="Previous page" onClick={() => handleChangePage(page - 1)} disabled={page === 0}><KeyboardArrowLeft /></IconButton>
        <Box sx={{ mx: 2 }}>Page {page + 1} of {Math.ceil(filteredResources.length / rowsPerPage)}</Box>
        <IconButton aria-label="Next page" onClick={() => handleChangePage(page + 1)} disabled={page >= Math.ceil(filteredResources.length / rowsPerPage) - 1}><KeyboardArrowRight /></IconButton>
        <IconButton aria-label="Last page" onClick={() => handleChangePage(Math.ceil(filteredResources.length / rowsPerPage) - 1)} disabled={page >= Math.ceil(filteredResources.length / rowsPerPage) - 1}><LastPage /></IconButton>
      </Box>
    </Box>
  );

  const renderCreateDialog = () => (
    <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
      <DialogTitle>Add New Resource</DialogTitle>
      <DialogContent>
        <TextField
          label="Resource Name" fullWidth margin="normal"
          value={newResource.resource}
          onChange={(e) => setNewResource({...newResource, resource: e.target.value})}
        />
        <TextField
          label="Description" fullWidth margin="normal"
          value={newResource.description}
          onChange={(e) => setNewResource({...newResource, description: e.target.value})}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Facility</InputLabel>
          <Select
            value={newResource.facility}
            onChange={(e) => setNewResource({...newResource, facility: e.target.value, unit: ''})}
            label="Facility"
          >
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>{facility.facility_name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel>Unit</InputLabel>
          <Select
            value={newResource.unit}
            onChange={(e) => setNewResource({...newResource, unit: e.target.value})}
            label="Unit"
            disabled={!newResource.facility}
          >
            {getUnitsForFacility(newResource.facility).map(unit => (
              <MenuItem key={unit._id} value={unit._id}>
                {unit.base_unit} ({unit.unit_short})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleCreate} variant="contained" color="primary">Add</Button>
      </DialogActions>
    </Dialog>
  );

  const renderEditDialog = () => (
    <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
      <DialogTitle>Edit Resource</DialogTitle>
      <DialogContent>
        <TextField
          label="Resource Name" fullWidth margin="normal"
          value={editingResource?.resource || ''}
          onChange={(e) => setEditingResource({...editingResource, resource: e.target.value})}
        />
        <TextField
          label="Description" fullWidth margin="normal"
          value={editingResource?.description || ''}
          onChange={(e) => setEditingResource({...editingResource, description: e.target.value})}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Facility</InputLabel>
          <Select
            value={getEditFacilityId()}
            onChange={(e) => setEditingResource({...editingResource, facility: e.target.value, unit: ''})}
            label="Facility"
          >
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>{facility.facility_name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel>Unit</InputLabel>
          <Select
            value={getEditUnitId()}
            onChange={(e) => setEditingResource({...editingResource, unit: e.target.value})}
            label="Unit"
          >
            {getUnitsForFacility(getEditFacilityId()).map(unit => (
              <MenuItem key={unit._id} value={unit._id}>
                {unit.base_unit} ({unit.unit_short})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleUpdate} variant="contained" color="primary">Save Changes</Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="resource-management">
      <h2>Resource Management</h2>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search resources..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 300 }}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} /> }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Facility</InputLabel>
          <Select value={selectedFacility} onChange={(e) => setSelectedFacility(e.target.value)} label="Filter by Facility">
            <MenuItem value="all">All Facilities</MenuItem>
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>{facility.facility_name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Button variant="contained" color="primary" onClick={() => setCreateDialogOpen(true)} sx={{ mb: 2 }}>
        Add New Resource
      </Button>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <Button variant="outlined" onClick={resetSort}>Reset Sort</Button>
      </Box>

      <table className="data-table">
        <thead>
          <tr>
            <SortableTableHeader column="resource" label="Resource" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <SortableTableHeader column="description" label="Description" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <SortableTableHeader column="facility.facility_name" label="Facility" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <th>Unit</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedResources.map(resource => (
            <tr key={resource._id}>
              <td>{resource.resource}</td>
              <td>{resource.description}</td>
              <td>{resource.facility?.facility_name}</td>
              <td>
                {resource.unit
                  ? `${resource.unit.base_unit} (${resource.unit.unit_short})`
                  : 'N/A'}
              </td>
              <td className="action-buttons">
                <Button
                  variant="contained" size="small"
                  className="row-action-edit"
                  style={{ marginRight: '8px' }}
                  onClick={() => { setEditingResource(resource); setEditDialogOpen(true); }}
                >
                  Edit
                </Button>
                <Button variant="contained" size="small" color="error" onClick={() => handleDelete(resource._id)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {renderPaginationControls()}
      {renderCreateDialog()}
      {renderEditDialog()}
    </div>
  );
};

export default ResourceManagement;
